import { computed, inject, Injectable } from '@angular/core';
import { AmlAdminErrorHandlerService } from '@core/services/aml-admin-error-handler.service';
import { TrainingUserListService } from '@features/academy/services/training-user-list.service';
import {
  TrainingUserListColumnSortKeyMap,
  TrainingUserListTableConfigService,
} from '@features/academy/services/training-user-list-table-config.service';
import { TrainingUserListFilterStore } from '@features/academy/stores/training-user-list-filter.store';
import { SortOrderMap } from '@kerberos-compliance/compliance-shared-ts/common/enums/sort-order.enum';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import type { ResponseTrainingUserDto } from '@kerberos-compliance/compliance-shared-ts/features/training/dtos/response.training-user.dto';
import type { Filtering, ListRequestQueryParameters, TableColumn } from '@kerberos-compliance/compliance-shared-ui';
import {
  BaseFacadeService,
  buildExportParameters,
  FileExportService,
  PageBasedPaginationStrategy,
} from '@kerberos-compliance/compliance-shared-ui';

type TrainingUserListSortKey = (typeof TrainingUserListColumnSortKeyMap)[keyof typeof TrainingUserListColumnSortKeyMap];

@Injectable()
export class TrainingUserListFacade extends BaseFacadeService<
  ResponseTrainingUserDto,
  never,
  Record<string, TrainingUserListSortKey>
> {
  protected override readonly componentName = 'TrainingUserListFacade';

  protected override readonly paginationStrategy = new PageBasedPaginationStrategy<ListRequestQueryParameters<never>>();

  protected override readonly defaultSort = {
    field: TrainingUserListColumnSortKeyMap.LastName,
    order: SortOrderMap.Ascending,
  };

  protected override readonly errorHandlerService = inject(AmlAdminErrorHandlerService);

  protected override readonly filterStore = inject(TrainingUserListFilterStore);

  private readonly trainingUserListService = inject(TrainingUserListService);

  private readonly tableConfigService = inject(TrainingUserListTableConfigService);

  private readonly fileExportService = inject(FileExportService);

  public readonly users = this.paginatedData;

  public readonly datatablePageIndex = this.currentPage;

  public readonly tableColumns = computed(() => this.tableConfigService.getUserPreferredColumns());

  public readonly showNoResultsErrorMessage = computed(
    () => this.hasCompletedInitialDataFetch() && this.users().length === 0 && !this.isLoading(),
  );

  /**
   * Sets the organization context for subsequent academy user fetches.
   *
   * @param {UUID | undefined} organizationId - The organization id, or undefined to clear.
   * @returns {void} This method does not return a value.
   */
  public setOrganizationId(organizationId: UUID | undefined): void {
    if (!organizationId) {
      return;
    }
    this.filterStore.setOrganizationId(organizationId);
  }

  /**
   * Loads academy users for the current organization context.
   *
   * @returns {Promise<void>} Resolves when the fetch completes.
   */
  public async loadUsers(): Promise<void> {
    await this.fetchData(this.buildFetchParameters());
  }

  /**
   * Persists the academy user table configuration.
   *
   * @param {TableColumn<ResponseTrainingUserDto>[]} columns - The current column configuration.
   * @returns {Promise<void>} Resolves once the configuration has been persisted.
   */
  public async saveTableConfiguration(columns: TableColumn<ResponseTrainingUserDto>[]): Promise<void> {
    await this.tableConfigService.saveTableConfiguration(columns);
  }

  /**
   * Exports the academy user list as an XLSX file using the current sort state.
   *
   * @param {Filtering} filterMode - The filtering mode to apply when exporting.
   * @returns {Promise<void>} Resolves once the export is complete.
   */
  public async exportAsXlsx(filterMode: Filtering): Promise<void> {
    const organizationId = this.filterStore.organizationId();
    if (!organizationId) {
      return;
    }

    await this.fileExportService.exportAsXlsxFile(
      filterMode,
      (filtering: Filtering) => {
        return buildExportParameters(filtering, {}, this.totalCount(), this.sortState()!, {
          organizationIds: [organizationId],
        });
      },
      this.trainingUserListService.buildTrainingUsersExportFileUrl(),
      'academy-users',
    );
  }

  /**
   * Prevents fetching until an organization id is available.
   *
   * @param {ListRequestQueryParameters<never>} parameters - The query parameters to evaluate.
   * @returns {boolean} True when fetching is allowed.
   */
  protected override canFetch(
    parameters: ListRequestQueryParameters<never>,
  ): parameters is ListRequestQueryParameters<never> {
    return super.canFetch(parameters) && this.filterStore.organizationId() !== undefined;
  }

  /**
   * Builds the (empty) filter payload for the academy users endpoint.
   *
   * @returns {never} The endpoint has no filters; returns undefined typed as never.
   */
  protected override buildFilters(): never {
    return undefined as never;
  }

  /**
   * Fetches academy users for the current organization using the provided pagination/sort parameters.
   *
   * @param {ListRequestQueryParameters<never>} parameters - Pagination and sort parameters.
   * @returns {Promise<void>} Resolves once the response has been applied to the store.
   */
  protected override async fetchData(parameters: ListRequestQueryParameters<never>): Promise<void> {
    await this.executeSafely(
      async () => {
        const organizationId = this.filterStore.organizationId();
        if (!organizationId) {
          return;
        }
        const response = await this.trainingUserListService.getTrainingUsers(organizationId, parameters);
        this.setPaginatedResponse(response, parameters.isLoadingMore ?? false);
      },
      `Fetching academy users for organizationId: ${this.filterStore.organizationId()}`,
      { onError: () => this.replacePaginatedResponse({ data: [], totalCount: 0, hasMore: false }) },
    );
  }
}
